import { LitElement, html } from 'lit'
import { ModalSaveMixin } from '../core/mixins/modal-save.mixin.js'
import { categoriaCacheService } from '../core/services/categoria-cache.service.js'

/**
 * Modal para criar e editar categorias de transações
 * Suporta tipos: Receita e Despesa
 * Inclui seleção de emoji para identificação visual
 * Impede alteração de tipo se categoria tem transações vinculadas
 *
 * Utiliza ModalSaveMixin para:
 * - Loading no botão Salvar com guard contra clique duplo
 * - Chamada direta ao categoriaCacheService (criar/atualizar)
 * - Exibição de erro inline com data-cy="erro-salvar-categoria"
 * - Guard de fechamento durante salvamento (_saving = true)
 * - Eventos cache-operation-started/finished via window
 *
 * Layout: flexbox com header fixo, conteúdo com scroll independente e footer fixo
 * com botões de ação sempre visíveis (padrão 040-modal-scroll-layout-padronizado)
 */
class ModalCategoria extends ModalSaveMixin(LitElement) {
  static properties = {
    ...super.properties,
    isEditMode: { type: Boolean },
    categoryData: { type: Object },
    fixedType: { type: String },
    hasTransactions: { type: Boolean }
  }

  /**
   * Nome da entidade para identificação nos eventos e no data-cy de erro.
   * Usado pelo ModalSaveMixin para gerar data-cy="erro-salvar-categoria".
   */
  _entidade = 'categoria'

  createRenderRoot() {
    return this
  }

  constructor() {
    super()
    this.open = false
    this.isEditMode = false
    this.categoryData = null
    this.fixedType = null
    this.hasTransactions = false
  }

  async show(categoryData = null, fixedType = null) {
    this.isEditMode = !!categoryData
    this.categoryData = categoryData
    this.fixedType = fixedType
    this.hasTransactions = false
    
    if (this.isEditMode && categoryData?.id) {
      try {
        // Simula verificação de transações
        this.hasTransactions = false
      } catch (error) {
        console.error('Erro ao verificar transações:', error)
      }
    }
    
    this.open = true
    
    if (this.isEditMode && categoryData) {
      setTimeout(() => {
        const nameInput = this.renderRoot.querySelector('[data-cy="input-nome"]')
        const emojiSelect = this.renderRoot.querySelector('[data-cy="select-emoji"]')
        const typeSelect = this.renderRoot.querySelector('[data-cy="select-tipo"]')
        
        if (nameInput) nameInput.value = categoryData.nome
        if (emojiSelect) emojiSelect.value = categoryData.emoji
        if (typeSelect) typeSelect.value = categoryData.tipo
      }, 0)
    } else if (this.fixedType) {
      setTimeout(() => {
        const typeSelect = this.renderRoot.querySelector('[data-cy="select-tipo"]')
        if (typeSelect) typeSelect.value = this.fixedType
      }, 0)
    }
  }

  /**
   * Fecha o modal ao clicar no backdrop (área escurecida)
   * Delega para this.close() que já gerencia o estado do modal
   */
  _handleBackdropClick() {
    this.close()
  }

  _stopPropagation(e) {
    e.stopPropagation()
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Salvar/Atualizar
   * Replica o padrão do floating-button com transição suave de cor
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onSaveHover(e) {
    e.target.style.backgroundColor = 'var(--hover-primary, #7B3EF0)'
  }

  /**
   * Restaura cor original do botão Salvar/Atualizar ao remover o mouse
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onSaveLeave(e) {
    e.target.style.backgroundColor = ''
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Cancelar
   * Usa background secundário para destacar o botão neutro
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onCancelHover(e) {
    e.target.style.backgroundColor = 'var(--glass-bg, rgba(117, 133, 255, 0.22))'
  }

  /**
   * Restaura cor original do botão Cancelar ao remover o mouse
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onCancelLeave(e) {
    e.target.style.backgroundColor = ''
  }

  _getEmojiOptions() {
    return [
      // Mais usados para categorias financeiras
      { name: 'Hambúrguer', emoji: '🍔' },
      { name: 'Pizza', emoji: '🍕' },
      { name: 'Cerveja', emoji: '🍻' },
      { name: 'Café', emoji: '☕' },
      { name: 'Casa', emoji: '🏠' },
      { name: 'Carro', emoji: '🚗' },
      { name: 'Combustível', emoji: '⛽' },
      { name: 'Dinheiro', emoji: '💰' },
      { name: 'Cartão', emoji: '💳' },
      { name: 'Pagamento', emoji: '💸' },
      { name: 'Seguro', emoji: '🛡️' },
      { name: 'Teatro', emoji: '🎭' },
      { name: 'Cinema', emoji: '🎥' },
      { name: 'Livros', emoji: '📚' },
      { name: 'Academia', emoji: '🏋️' },
      { name: 'Remédio', emoji: '💊' },
      { name: 'Camisa', emoji: '👕' },
      { name: 'Calça', emoji: '👖' },
      { name: 'Sapato', emoji: '👟' },
      { name: 'Presente', emoji: '🎁' },
      { name: 'Viagem', emoji: '✈️' },
      { name: 'Hotel', emoji: '🏨' },
      { name: 'Escola', emoji: '🏫' },
      { name: 'Hospital', emoji: '🏥' },
      { name: 'Celular', emoji: '📱' },
      { name: 'Computador', emoji: '💻' },
      { name: 'Energia', emoji: '🔌' },
      { name: 'Ferramenta', emoji: '🛠️' },
      { name: 'Música', emoji: '🎵' },
      { name: 'Arte', emoji: '🎨' },
      { name: 'Trabalho', emoji: '💼' },
      { name: 'Gráfico', emoji: '📈' },
      
      // Rostos e emoções
      { name: 'Sorriso', emoji: '😀' },
      { name: 'Alegre', emoji: '😃' },
      { name: 'Feliz', emoji: '😄' },
      { name: 'Rindo', emoji: '😁' },
      { name: 'Gargalhada', emoji: '😆' },
      { name: 'Chorando de rir', emoji: '😂' },
      { name: 'Piscadela', emoji: '😉' },
      { name: 'Tímido', emoji: '😊' },
      { name: 'Apaixonado', emoji: '😍' },
      { name: 'Beijinho', emoji: '😘' },
      { name: 'Pensativo', emoji: '🤔' },
      { name: 'Neutro', emoji: '😐' },
      { name: 'Triste', emoji: '😢' },
      { name: 'Bravo', emoji: '😠' },
      { name: 'Surpreso', emoji: '😲' },
      
      // Corações e símbolos
      { name: 'Coração', emoji: '❤️' },
      { name: 'Coração laranja', emoji: '🧡' },
      { name: 'Coração amarelo', emoji: '💛' },
      { name: 'Coração verde', emoji: '💚' },
      { name: 'Coração azul', emoji: '💙' },
      { name: 'Coração roxo', emoji: '💜' },
      { name: 'Estrela', emoji: '⭐' },
      { name: 'Brilho', emoji: '✨' },
      { name: 'Fogo', emoji: '🔥' },
      { name: 'Raio', emoji: '⚡' },
      
      // Natureza
      { name: 'Sol', emoji: '☀️' },
      { name: 'Lua', emoji: '🌙' },
      { name: 'Nuvem', emoji: '☁️' },
      { name: 'Chuva', emoji: '🌧️' },
      { name: 'Arco-íris', emoji: '🌈' },
      { name: 'Árvore', emoji: '🌳' },
      { name: 'Flor', emoji: '🌸' },
      { name: 'Rosa', emoji: '🌹' },
      { name: 'Girassol', emoji: '🌻' },
      { name: 'Folha', emoji: '🍃' },
      { name: 'Água', emoji: '💧' },
      
      // Animais
      { name: 'Gato', emoji: '🐱' },
      { name: 'Cachorro', emoji: '🐶' },
      { name: 'Pássaro', emoji: '🐦' },
      { name: 'Peixe', emoji: '🐟' },
      { name: 'Borboleta', emoji: '🦋' },
      { name: 'Abelha', emoji: '🐝' },
      { name: 'Leão', emoji: '🦁' },
      { name: 'Tigre', emoji: '🐯' },
      { name: 'Urso', emoji: '🐻' },
      { name: 'Panda', emoji: '🐼' },
      { name: 'Coelho', emoji: '🐰' },
      { name: 'Raposa', emoji: '🦊' },
      { name: 'Lobo', emoji: '🐺' },
      { name: 'Unicórnio', emoji: '🦄' },
      
      // Comidas
      { name: 'Maçã', emoji: '🍎' },
      { name: 'Banana', emoji: '🍌' },
      { name: 'Laranja', emoji: '🍊' },
      { name: 'Uva', emoji: '🍇' },
      { name: 'Morango', emoji: '🍓' },
      { name: 'Cereja', emoji: '🍒' },
      { name: 'Pêssego', emoji: '🍑' },
      { name: 'Abacaxi', emoji: '🍍' },
      { name: 'Melancia', emoji: '🍉' },
      { name: 'Pão', emoji: '🍞' },
      { name: 'Croissant', emoji: '🥐' },
      { name: 'Queijo', emoji: '🧀' },
      { name: 'Ovo', emoji: '🥚' },
      { name: 'Bacon', emoji: '🥓' },
      { name: 'Frango', emoji: '🍗' },
      { name: 'Carne', emoji: '🥩' },
      { name: 'Taco', emoji: '🌮' },
      { name: 'Burrito', emoji: '🌯' },
      { name: 'Salada', emoji: '🥗' },
      { name: 'Sopa', emoji: '🍲' },
      { name: 'Macarrão', emoji: '🍝' },
      { name: 'Sushi', emoji: '🍣' },
      { name: 'Sorvete', emoji: '🍦' },
      { name: 'Bolo', emoji: '🍰' },
      { name: 'Cupcake', emoji: '🧁' },
      { name: 'Doce', emoji: '🍭' },
      { name: 'Chocolate', emoji: '🍫' },
      { name: 'Cookie', emoji: '🍪' },
      { name: 'Donut', emoji: '🍩' },
      
      // Bebidas
      { name: 'Leite', emoji: '🥛' },
      { name: 'Chá', emoji: '🍵' },
      { name: 'Suco', emoji: '🧃' },
      { name: 'Refrigerante', emoji: '🥤' },
      { name: 'Vinho', emoji: '🍷' },
      { name: 'Champagne', emoji: '🍾' },
      { name: 'Taça', emoji: '🍸' },
      { name: 'Coquetel', emoji: '🍹' },
      
      // Objetos
      { name: 'Garfo', emoji: '🍴' },
      { name: 'Faca', emoji: '🔪' },
      { name: 'Colher', emoji: '🥄' },
      { name: 'Prato', emoji: '🍽️' },
      { name: 'Xícara', emoji: '☕' },
      { name: 'Garrafa', emoji: '🍼' },
      
      // Transporte
      { name: 'Bicicleta', emoji: '🚲' },
      { name: 'Moto', emoji: '🏍️' },
      { name: 'Ônibus', emoji: '🚌' },
      { name: 'Trem', emoji: '🚆' },
      { name: 'Avião', emoji: '✈️' },
      { name: 'Helicóptero', emoji: '🚁' },
      { name: 'Navio', emoji: '🚢' },
      { name: 'Foguete', emoji: '🚀' },
      
      // Lugares
      { name: 'Prédio', emoji: '🏢' },
      { name: 'Loja', emoji: '🏪' },
      { name: 'Banco', emoji: '🏦' },
      { name: 'Igreja', emoji: '⛪' },
      { name: 'Castelo', emoji: '🏰' },
      { name: 'Estádio', emoji: '🏟️' },
      { name: 'Parque', emoji: '🏞️' },
      { name: 'Praia', emoji: '🏖️' },
      { name: 'Montanha', emoji: '⛰️' },
      
      // Atividades
      { name: 'Futebol', emoji: '⚽' },
      { name: 'Basquete', emoji: '🏀' },
      { name: 'Tênis', emoji: '🎾' },
      { name: 'Natação', emoji: '🏊' },
      { name: 'Corrida', emoji: '🏃' },
      { name: 'Dança', emoji: '💃' },
      { name: 'Violão', emoji: '🎸' },
      { name: 'Piano', emoji: '🎹' },
      { name: 'Microfone', emoji: '🎤' },
      { name: 'Fone', emoji: '🎧' },
      { name: 'Câmera', emoji: '📷' },
      { name: 'Filme', emoji: '🎬' },
      { name: 'Jogo', emoji: '🎮' },
      
      // Tecnologia
      { name: 'Relógio', emoji: '⌚' },
      { name: 'Tablet', emoji: '📱' },
      { name: 'Mouse', emoji: '🖱️' },
      { name: 'Teclado', emoji: '⌨️' },
      { name: 'Impressora', emoji: '🖨️' },
      { name: 'Disquete', emoji: '💾' },
      { name: 'CD', emoji: '💿' },
      { name: 'DVD', emoji: '📀' },
      { name: 'Bateria', emoji: '🔋' },
      { name: 'Lâmpada', emoji: '💡' },
      { name: 'Lanterna', emoji: '🔦' },
      
      // Roupas e acessórios
      { name: 'Óculos', emoji: '👓' },
      { name: 'Óculos escuros', emoji: '🕶️' },
      { name: 'Gravata', emoji: '👔' },
      { name: 'Vestido', emoji: '👗' },
      { name: 'Biquíni', emoji: '👙' },
      { name: 'Chapéu', emoji: '👒' },
      { name: 'Boné', emoji: '🧢' },
      { name: 'Coroa', emoji: '👑' },
      { name: 'Anel', emoji: '💍' },
      { name: 'Bolsa', emoji: '👜' },
      { name: 'Mochila', emoji: '🎒' },
      
      // Símbolos e outros
      { name: 'Bandeira', emoji: '🏁' },
      { name: 'Troféu', emoji: '🏆' },
      { name: 'Medalha', emoji: '🏅' },
      { name: 'Chave', emoji: '🔑' },
      { name: 'Cadeado', emoji: '🔒' },
      { name: 'Martelo', emoji: '🔨' },
      { name: 'Chave inglesa', emoji: '🔧' },
      { name: 'Parafuso', emoji: '🔩' },
      { name: 'Lupa', emoji: '🔍' },
      { name: 'Bomba', emoji: '💣' },
      { name: 'Remédio', emoji: '💊' },
      { name: 'Seringa', emoji: '💉' },
      { name: 'Termômetro', emoji: '🌡️' },
      { name: 'Balança', emoji: '⚖️' },
      { name: 'Ampulheta', emoji: '⏳' },
      { name: 'Despertador', emoji: '⏰' },
      { name: 'Calendário', emoji: '📅' },
      { name: 'Livro', emoji: '📖' },
      { name: 'Caderno', emoji: '📓' },
      { name: 'Lápis', emoji: '✏️' },
      { name: 'Caneta', emoji: '✒️' },
      { name: 'Pincel', emoji: '🖌️' },
      { name: 'Régua', emoji: '📏' },
      { name: 'Tesoura', emoji: '✂️' },
      { name: 'Clipe', emoji: '📎' },
      { name: 'Pasta', emoji: '📁' },
      { name: 'Lixeira', emoji: '🗑️' },
      { name: 'Email', emoji: '📧' },
      { name: 'Carta', emoji: '💌' },
      { name: 'Pacote', emoji: '📦' },
      { name: 'Etiqueta', emoji: '🏷️' }
    ]
  }

  /**
   * Executa a operação de salvamento via categoriaCacheService.
   * Utiliza _executeSave do ModalSaveMixin que gerencia:
   * - Guard contra clique duplo (_saving = true → retorna)
   * - Loading no botão Salvar
   * - Timeout de 30s via Promise.race
   * - Disparo de eventos cache-operation-started/finished
   * - Fechamento do modal após sucesso
   * - Exibição de erro inline após falha
   *
   * @returns {Promise<void>}
   */
  async _handleSave() {
    const nameInput = this.renderRoot.querySelector('[data-cy="input-nome"]')
    const emojiSelect = this.renderRoot.querySelector('[data-cy="select-emoji"]')
    const typeSelect = this.renderRoot.querySelector('[data-cy="select-tipo"]')

    const dados = {
      nome: nameInput.value,
      emoji: emojiSelect.value,
      tipo: typeSelect.value
    }

    const operacao = this.isEditMode ? 'atualizar' : 'criar'
    const id = this.isEditMode ? this.categoryData?.id : undefined

    await this._executeSave(categoriaCacheService, operacao, dados, id)
  }

  render() {
    return html`
      <div 
        class="modal ${this.open ? 'is-active' : ''}" 
        @click="${this._handleBackdropClick}"
        style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 20000;
          display: ${this.open ? 'flex' : 'none'};
          align-items: center;
          justify-content: center;
        "
      >
        <div 
          class="card"
          @click="${this._stopPropagation}"
          style="
            width: 90%;
            max-width: 500px;
            max-height: 85vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border-radius: var(--radius-card, 20px);
            box-shadow: var(--shadow-card, 0 10px 40px rgba(0,0,0,0.35));
            position: relative;
          "
        >
          <!-- Zona 1: Header fixo no topo com sombra sobre o conteúdo -->
          <header class="card-header" style="flex-shrink: 0; position: relative; z-index: 2; box-shadow: var(--shadow-header-modal, 0 4px 12px rgba(0,0,0,0.3));">
            ${this.renderBotaoVoltarIOS()}
            <p class="card-header-title" data-cy="modal-categoria-title">
              ${this.isEditMode ? 'Editar Categoria' : 'Adicionar Categoria'}
            </p>
            ${this.renderBotaoFechar('modal-categoria-close')}
          </header>

          <!-- Zona 2: Conteúdo com scroll independente -->
          <div class="card-content custom-scrollbar" @input="${this._clearError}" @change="${this._clearError}" style="
            flex: 1;
            overflow-y: auto;
            overflow-x: hidden;
            min-height: 0;
          ">
            <div class="field mb-4">
              <label class="label is-size-7 has-text-weight-medium">Nome</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  placeholder="Ex: Alimentação"
                  required
                  data-cy="input-nome"
                />
              </div>
            </div>

            <div class="field mb-4">
              <label class="label is-size-7 has-text-weight-medium">Ícone (Emoji)</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select data-cy="select-emoji">
                    <option value="">Selecione um emoji</option>
                    ${this._getEmojiOptions().map(option => html`
                      <option value="${option.emoji}">${option.name} ${option.emoji}</option>
                    `)}
                  </select>
                </div>
              </div>
            </div>

            <div class="field mb-4">
              <label class="label is-size-7 has-text-weight-medium">Tipo</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select data-cy="select-tipo" ?disabled="${this.fixedType || (this.isEditMode && this.hasTransactions)}">
                    <option value="Despesa">Despesa</option>
                    <option value="Receita">Receita</option>
                  </select>
                </div>
              </div>
              ${this.isEditMode && this.hasTransactions ? html`
                <p class="help is-warning">
                  <span class="icon is-small">
                    <i class="fas fa-exclamation-triangle"></i>
                  </span>
                  Não é possível alterar o tipo pois existem transações vinculadas a esta categoria
                </p>
              ` : ''}
            </div>

            ${this.renderErroSalvar()}
          </div>

          <!-- Zona 3: Footer fixo com botões de ação e sombra sobre o conteúdo -->
          <footer class="card-footer"
            data-cy="footer-modal-categoria"
            aria-label="Ações do modal de categoria"
            style="
              flex-shrink: 0;
              padding: 1rem 1.5rem;
              background-color: var(--bg-surface, #151C30);
              border-top: 1px solid var(--border, #26304A);
              position: relative;
              z-index: 2;
              box-shadow: var(--shadow-footer-modal, 0 -4px 12px rgba(0,0,0,0.3));
            "
          >
            <div class="field is-grouped is-justify-content-flex-end" style="width: 100%">
              <div class="control">
                <button 
                  class="button" 
                  type="button" 
                  data-cy="btn-cancelar-categoria"
                  ?disabled="${this._saving}"
                  @click="${this.close}"
                  @mouseenter="${this._onCancelHover}"
                  @mouseleave="${this._onCancelLeave}"
                >
                  Cancelar
                </button>
              </div>
              <div class="control">
                <button 
                  class="button is-primary ${this._saving ? 'is-loading' : ''}" 
                  type="button" 
                  data-cy="btn-salvar-categoria"
                  ?disabled="${this._saving}"
                  @click="${this._handleSave}"
                  @mouseenter="${this._onSaveHover}"
                  @mouseleave="${this._onSaveLeave}"
                >
                  ${this.isEditMode ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </div>
          </footer>
        </div>
      </div>
    `
  }
}

customElements.define('modal-categoria', ModalCategoria)